// TrackFind.jsx - For the person who deleted the email. Two ways back in: we send
// the link to their email, or they prove the phone number with a six digit code.
//
// Neither path ever confirms whether an email or number is on an order, so this
// page cannot be used to find out who has sold us something.

import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { Seo } from "../lib/seo.jsx";
import { publicApi } from "../lib/api.js";
import { remember, remembered } from "../lib/session.js";
import { useForm } from "../lib/hooks.js";
import { Button, Input, Alert, Panel } from "../ui/kit.jsx";
import { Envelope, Message, ArrowRight, Phone } from "../ui/icons.jsx";
import { Reveal } from "../ui/motion.jsx";
import { TruckRoute } from "../ui/illustrations.jsx";

export default function TrackFind() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [channel, setChannel] = useState("sms");
  const [challenge, setChallenge] = useState(null);
  const [sentTo, setSentTo] = useState("");

  // Somebody who has already proved an order on this browser goes straight to
  // it. Making a customer request a code every time they want to look at their
  // own order is the whole reason people give up and phone instead.
  //
  // After mount, never during render: this page is prerendered, and a redirect
  // during render would be a hydration mismatch as well as a page crawlers
  // could not read. `?switch=1` skips it, which is the way back for a shared
  // computer and for anybody with a second order.
  useEffect(() => {
    if (params.get("switch")) return;
    const kept = remembered();
    if (kept) navigate(`/track/${kept.token}`, { replace: true });
  }, [params, navigate]);

  const request = useForm({ identifier: "" });
  const confirm = useForm({ code: "" });

  const onRequest = request.submit(async (values) => {
    const result = await publicApi.recover({ channel, identifier: values.identifier });
    setSentTo(values.identifier);
    if (channel === "sms" && result.challenge) setChallenge(result.challenge);
    else request.setMessage({ kind: "success", text: result.message });
    return result;
  });

  const onConfirm = confirm.submit(async (values) => {
    const result = await publicApi.verifyRecovery({ challenge, code: values.code });
    // A code off their own phone is the strongest proof this page can ask for,
    // so this browser keeps the order and stops asking.
    remember(result);
    navigate(`/track/${result.token}`);
    return result;
  });

  return (
    <>
      <Seo
        title="Track your order"
        path="/track"
        description="Find the tracking page for your International Gold and Silver order. We text or email the link back to you."
      />

      <div className="shell-narrow py-12 md:py-20">
        <Reveal as="p" from="none" className="eyebrow mb-4">Track an order</Reveal>
        <Reveal as="h1" delay={60} className="display display-lg text-ink">Lost your link?</Reveal>
        <Reveal as="p" delay={120} className="lede mt-5 text-muted">
          Every email and text we send has a link straight to your order. If you cannot find one,
          we will send it again.
        </Reveal>

        <Reveal delay={180}>
          <TruckRoute className="max-w-[15rem] mt-8 mb-2 opacity-90" />
        </Reveal>

        <Panel className="p-5 sm:p-7 mt-4">
          {challenge ? (
            <form onSubmit={onConfirm} noValidate>
              <p className="eyebrow mb-2">Step two</p>
              <h2 className="display display-sm text-ink mb-1">Enter the code</h2>
              <p className="hint mb-5">
                We texted a six digit code to {sentTo}. It is good for ten minutes.
              </p>

              <Input
                label="Six digit code"
                inputMode="numeric"
                maxLength={6}
                autoComplete="one-time-code"
                autoFocus
                className="text-2xl tracking-[0.4em] text-center num"
                {...confirm.field("code")}
              />

              {confirm.message && <Alert tone="error" className="mt-4">{confirm.message.text}</Alert>}

              <div className="flex items-center gap-3 mt-6">
                <Button type="submit" loading={confirm.busy}>Open my order <ArrowRight size={16} /></Button>
                <Button type="button" variant="ghost" onClick={() => { setChallenge(null); confirm.setMessage(null); }}>
                  Use a different number
                </Button>
              </div>
            </form>
          ) : (
            <form onSubmit={onRequest} noValidate>
              <div className="flex gap-1 mb-6">
                {[
                  { value: "sms", label: "Text me the link", icon: Message },
                  { value: "email", label: "Email me the link", icon: Envelope },
                ].map(({ value, label, icon: Glyph }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => { setChannel(value); request.setMessage(null); }}
                    className={`btn btn-sm ${channel === value ? "btn-ink" : "btn-quiet"}`}
                  >
                    <Glyph size={15} /> {label}
                  </button>
                ))}
              </div>

              <Input
                key={channel}
                label={channel === "sms" ? "Mobile number on the order" : "Email address on the order"}
                type={channel === "sms" ? "tel" : "email"}
                autoComplete={channel === "sms" ? "tel" : "email"}
                placeholder={channel === "sms" ? "(401) 315-4653" : "you@example.com"}
                autoFocus
                {...request.field("identifier")}
              />

              {request.message && (
                <Alert tone={request.message.kind === "success" ? "success" : "error"} className="mt-4">
                  {request.message.text}
                </Alert>
              )}

              <Button type="submit" loading={request.busy} className="mt-6">
                {channel === "sms" ? "Text me a code" : "Email me the link"}
              </Button>

              <p className="hint mt-4 leading-relaxed">
                For your protection we never say whether an address or number is on an order.
                If it is, the message is already on its way.
              </p>
            </form>
          )}
        </Panel>

        <p className="hint mt-8 text-center">
          Still stuck? Call{" "}
          <a href="tel:+1-401-315-4653" className="link text-ink font-semibold">(401) 315-4653</a>
          <Phone size={13} className="inline ml-1.5 -mt-0.5 text-faint" />
        </p>
      </div>
    </>
  );
}
